import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertCircle,
  CheckCircle2,
  ChevronDown,
  PackageCheck,
  Save,
  Truck,
  X,
} from 'lucide-react';
import { toast } from 'sonner';
import { useIsReadOnly } from '@/shared/auth/SessionProvider';
import { Button } from '@/shared/components/ui/button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog';
import { Input } from '@/shared/components/ui/input';
import { Label } from '@/shared/components/ui/label';
import { Skeleton } from '@/shared/components/ui/skeleton';
import { FileLink } from '@/shared/components/file-upload/FileLink';
import { FileUploader } from '@/shared/components/file-upload/FileUploader';
import { listEntityFiles } from '@/shared/api/documents';
import { cn } from '@/shared/lib/cn';
import {
  anularDetalleRecepcion,
  getRecepcionDetail,
  recibirRecepcion,
  type DetalleRow,
  type RecepcionRow,
} from '../api';

/**
 * Detail view for a recepción. Shows each line's expected vs received
 * quantity, lets admin edit received quantities + serials, enter a
 * shipment number, anular lines, and finalize the recepción.
 */
export function DetalleRecepcionDialog({
  summary,
  onClose,
}: {
  summary: RecepcionRow;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const detailQ = useQuery({
    queryKey: ['recepcion', 'detail', summary.id],
    queryFn: () => getRecepcionDetail(summary.id),
    staleTime: 15_000,
  });
  // Multi-file mode: enumerate every drive item under
  // `Recepciones/{summary.id}/`. The folder is the source of truth —
  // re-runs after each upload/delete via `qc.invalidateQueries` so the
  // list updates without a full page reload.
  const filesQ = useQuery({
    queryKey: ['recepcion', 'files', summary.id],
    queryFn: () => listEntityFiles('Recepcion', summary.id),
    staleTime: 15_000,
  });
  const refreshFiles = () =>
    qc.invalidateQueries({ queryKey: ['recepcion', 'files', summary.id] });

  const isVisitante = useIsReadOnly();
  const readOnly =
    isVisitante ||
    (summary.fields.Status_RR ?? '') === 'Recibido' ||
    (summary.fields.Status_RR ?? '') === 'Anulado';

  // Per-line state: map detalleId → { recibida, nroSerie }
  // Documentación section is collapsable so a recepción with many
  // attachments doesn't push the rest of the dialog (líneas + finalize
  // banner) below the fold. Open by default — the file list itself
  // scrolls inside a max-height window when there are more than ~3-4
  // files.
  const [docsOpen, setDocsOpen] = useState(true);
  const [edits, setEdits] = useState<
    Record<string, { cantidadRecibida: string; nroSerie: string }>
  >({});
  const [nroDespacho, setNroDespacho] = useState('');

  useEffect(() => {
    if (!detailQ.data) return;
    setEdits((prev) => {
      if (Object.keys(prev).length > 0) return prev;
      const initial: Record<string, { cantidadRecibida: string; nroSerie: string }> = {};
      for (const d of detailQ.data.detalles) {
        if ((d.fields.Status_DR ?? '') === 'Anulado') continue;
        initial[d.id] = {
          cantidadRecibida: String(d.cantidadRecibida),
          nroSerie: (d.fields.NroSerie_DR ?? '').replace(/^-$/, ''),
        };
      }
      return initial;
    });
    // Default despacho from any detalle that already has one (usually all share it).
    const firstDespacho = detailQ.data.detalles.find(
      (d) => d.fields.NroDespacho_DR && d.fields.NroDespacho_DR !== '-',
    )?.fields.NroDespacho_DR;
    if (firstDespacho && !nroDespacho) setNroDespacho(firstDespacho);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detailQ.data]);

  const activeDetalles = useMemo(
    () =>
      (detailQ.data?.detalles ?? []).filter(
        (d) => (d.fields.Status_DR ?? '') !== 'Anulado',
      ),
    [detailQ.data],
  );
  const anuladas = useMemo(
    () =>
      (detailQ.data?.detalles ?? []).filter(
        (d) => (d.fields.Status_DR ?? '') === 'Anulado',
      ),
    [detailQ.data],
  );

  const dirty = useMemo(() => {
    if (!detailQ.data) return false;
    for (const d of activeDetalles) {
      const edit = edits[d.id];
      if (!edit) continue;
      const editedQty = parseNumber(edit.cantidadRecibida);
      const editedSerie = edit.nroSerie.trim();
      const originalSerie = (d.fields.NroSerie_DR ?? '').replace(/^-$/, '');
      if (
        Math.abs(editedQty - d.cantidadRecibida) > 0.0001 ||
        editedSerie !== originalSerie
      ) {
        return true;
      }
    }
    // Despacho change also counts.
    const originalDespacho = (
      detailQ.data.detalles.find((d) => d.fields.NroDespacho_DR !== '-')?.fields
        .NroDespacho_DR ?? ''
    ).replace(/^-$/, '');
    if (nroDespacho.trim() !== originalDespacho) return true;
    return false;
  }, [activeDetalles, edits, nroDespacho, detailQ.data]);

  const saveMutation = useMutation({
    mutationFn: (finalize: boolean) => {
      const detalles = activeDetalles.map((d) => ({
        detalleId: d.id,
        cantidadRecibida: parseNumber(edits[d.id]?.cantidadRecibida ?? String(d.cantidadRecibida)),
        nroSerie: edits[d.id]?.nroSerie?.trim() || undefined,
      }));
      return recibirRecepcion(summary.id, {
        nroDespacho: nroDespacho.trim() || undefined,
        detalles,
        finalize,
      });
    },
    onSuccess: (r, finalize) => {
      if (r.outcome === 'updated') {
        toast.success(finalize ? 'Recepción finalizada' : 'Cambios guardados');
        qc.invalidateQueries({ queryKey: ['recepcion'] });
        if (finalize) onClose();
      } else {
        toast.info('No aplicable', { description: r.message });
      }
    },
    onError: (err) =>
      toast.error('No se pudo guardar', {
        description: err instanceof Error ? err.message : 'Error desconocido',
      }),
  });

  const anularMutation = useMutation({
    mutationFn: (detalleId: string) =>
      anularDetalleRecepcion(summary.id, detalleId),
    onSuccess: (r) => {
      if (r.outcome === 'anulado' || r.outcome === 'already_anulado') {
        // When the backend reports the header was auto-anulated (last
        // remaining line just went), close the dialog so the user lands
        // back on the gallery and sees the row flip to Anulado.
        const headerAnulada = r.outcome === 'anulado' && r.recepcionAnulada;
        toast.success(
          headerAnulada
            ? 'Recepción anulada (no quedaban líneas activas)'
            : 'Línea anulada',
        );
        qc.invalidateQueries({ queryKey: ['recepcion'] });
        if (headerAnulada) onClose();
      }
    },
    onError: (err) =>
      toast.error('No se pudo anular la línea', {
        description: err instanceof Error ? err.message : 'Error desconocido',
      }),
  });

  // Confirmation gate when the user is about to anular the LAST active
  // line — without it, the user lands on a recepción with zero líneas and
  // /finalizar fails with invalid_body (server requires at least one
  // detalle). The backend now also auto-anulates the header in that case;
  // this dialog just makes sure the user knows it's about to happen.
  const [confirmAnular, setConfirmAnular] = useState<DetalleRow | null>(null);
  const handleAnularRequest = (detalle: DetalleRow) => {
    if (activeDetalles.length === 1 && detalle.id === activeDetalles[0]?.id) {
      setConfirmAnular(detalle);
      return;
    }
    anularMutation.mutate(detalle.id);
  };

  const busy = saveMutation.isPending || anularMutation.isPending;

  return (
    <Dialog open onOpenChange={(o) => !o && !busy && onClose()}>
      <DialogContent
        className="max-w-4xl"
        processing={
          saveMutation.isPending
            ? saveMutation.variables
              ? 'Finalizando recepción…'
              : 'Guardando cambios…'
            : false
        }
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Truck className="h-4 w-4" aria-hidden />
            Recepción #{summary.id}
            {summary.compra && (
              <span className="text-xs font-normal text-muted-foreground">
                · Compra #{summary.compra.id}
                {summary.compra.nroFactura && ` · Fc ${summary.compra.nroFactura}`}
              </span>
            )}
          </DialogTitle>
          <DialogDescription className="flex flex-wrap items-center gap-2">
            <StatusBadge status={(summary.fields.Status_RR ?? '') as string} />
            {summary.compra?.proveedor && (
              <span className="text-sm">{summary.compra.proveedor}</span>
            )}
          </DialogDescription>
        </DialogHeader>
        <DialogBody className="space-y-4">
          {detailQ.isPending ? (
            <div className="space-y-2">
              {Array.from({ length: 3 }).map((_, i) => (
                <Skeleton key={i} className="h-20 w-full" />
              ))}
            </div>
          ) : detailQ.isError ? (
            <div className="rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm text-destructive">
              No se pudo cargar el detalle.
            </div>
          ) : (
            <>
              <div className="space-y-2 rounded-lg border bg-muted/20 p-3">
                <div className="flex flex-wrap items-baseline justify-between gap-3">
                  <div>
                    <Label htmlFor="rr-despacho" className="text-sm">
                      Nº Despacho / Remito
                    </Label>
                    <Input
                      id="rr-despacho"
                      className="mt-1"
                      value={nroDespacho}
                      onChange={(e) => setNroDespacho(e.target.value)}
                      placeholder="Nº remito del proveedor"
                      maxLength={120}
                      disabled={readOnly || busy}
                    />
                  </div>
                  <HeaderSummary detalles={activeDetalles} />
                </div>
                {/* Documentación adjunta (remito / foto / inspección).
                    Multi-file: every drive item under
                    `DocumentosRecepciones/Recepciones/{recepcionId}/`
                    is surfaced as its own pill. Editing is locked on
                    finalized (Recibido) and anulado recepciones — the
                    pills stay visible for audit, but new uploads /
                    deletes are gated on `!readOnly`.
                    Collapsable + scrollable inside so a recepción with
                    many attachments doesn't bloat the dialog. */}
                <div className="min-w-0">
                  <button
                    type="button"
                    onClick={() => setDocsOpen((v) => !v)}
                    className="flex w-full items-center gap-1 text-left"
                    aria-expanded={docsOpen}
                  >
                    <ChevronDown
                      className={cn(
                        'h-3.5 w-3.5 shrink-0 text-muted-foreground transition-transform',
                        !docsOpen && '-rotate-90',
                      )}
                      aria-hidden
                    />
                    <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                      Documentación
                      {filesQ.data && filesQ.data.length > 0
                        ? ` (${filesQ.data.length})`
                        : ''}
                    </span>
                  </button>
                  {docsOpen && (
                    <div className="mt-1.5 w-full min-w-0">
                      {/* Cap the file list at ~4 rows of compact pills
                          (≈40px each) before scrolling. The uploader
                          stays outside the scroll container so it's
                          always reachable. */}
                      <div className="flex max-h-44 flex-col gap-1.5 overflow-y-auto pr-1">
                        {filesQ.isPending ? (
                          <p className="text-xs text-muted-foreground">
                            Cargando archivos…
                          </p>
                        ) : filesQ.data && filesQ.data.length > 0 ? (
                          filesQ.data.map((f) => (
                            <FileLink
                              key={f.driveItemId}
                              entityType="Recepcion"
                              entityId={summary.id}
                              driveItemId={f.driveItemId}
                              fileName={f.fileName}
                              allowDelete={!readOnly}
                              onDeleted={refreshFiles}
                              compact
                            />
                          ))
                        ) : readOnly ? (
                          <p className="text-xs text-muted-foreground">
                            Sin documentación adjunta.
                          </p>
                        ) : null}
                      </div>
                      {!readOnly && (
                        <div className="mt-1.5">
                          <FileUploader
                            entityType="Recepcion"
                            entityId={summary.id}
                            compact
                            emptyHint={
                              filesQ.data && filesQ.data.length > 0
                                ? 'Agregar otro archivo'
                                : 'Subir remito, foto u otro documento'
                            }
                            onUploaded={refreshFiles}
                          />
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>

              <section className="space-y-2">
                <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  Líneas ({activeDetalles.length})
                </h3>
                <div className="space-y-2">
                  {activeDetalles.map((d) => (
                    <LineCard
                      key={d.id}
                      detalle={d}
                      edit={edits[d.id]}
                      onEditChange={(next) =>
                        setEdits((prev) => ({ ...prev, [d.id]: next }))
                      }
                      onAnular={() => handleAnularRequest(d)}
                      readOnly={readOnly}
                      busy={busy}
                    />
                  ))}
                </div>
              </section>

              {anuladas.length > 0 && (
                <section className="space-y-2">
                  <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    Líneas anuladas ({anuladas.length})
                  </h3>
                  <div className="space-y-1">
                    {anuladas.map((d) => (
                      <div
                        key={d.id}
                        className="flex items-center justify-between rounded-md border bg-muted/30 px-3 py-1.5 text-sm text-muted-foreground line-through"
                      >
                        <span>{d.fields.Articulo_DR || '—'}</span>
                        <span className="text-xs">
                          {d.cantidadEsperada} un.
                        </span>
                      </div>
                    ))}
                  </div>
                </section>
              )}

              {!readOnly && (
                <div className="flex items-start gap-2 rounded-md border border-sky-200 bg-sky-50 px-3 py-2 text-xs text-sky-800">
                  <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
                  <span>
                    Al finalizar, la recepción queda cerrada. El stock pasa de
                    {' '}
                    <em>Por Ingresar</em> a <em>Disponible</em> desde el módulo
                    Inventario.
                  </span>
                </div>
              )}
            </>
          )}
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={busy}>
            Cerrar
          </Button>
          {!readOnly && (
            <>
              <Button
                variant="outline"
                onClick={() => saveMutation.mutate(false)}
                disabled={!dirty || busy}
                className="gap-2"
              >
                <Save className="h-4 w-4" aria-hidden />
                Guardar cambios
              </Button>
              <Button
                onClick={() => saveMutation.mutate(true)}
                disabled={busy}
                className="gap-2"
              >
                <CheckCircle2 className="h-4 w-4" aria-hidden />
                Finalizar recepción
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>

      {/* Last-line guard. Backend auto-anulates the header when this is
          the last active line; we surface that here so the user knows
          it's about to happen rather than discovering it via the
          invalid_body that the old flow produced on Finalizar. */}
      {confirmAnular && (
        <Dialog
          open
          onOpenChange={(o) => !o && !anularMutation.isPending && setConfirmAnular(null)}
        >
          <DialogContent
            className="max-w-md"
            processing={anularMutation.isPending && 'Anulando recepción…'}
          >
            <DialogHeader>
              <DialogTitle>¿Anular la última línea?</DialogTitle>
              <DialogDescription>
                Es la única línea activa. Si la anulás, la recepción se marcará
                automáticamente como <strong>Anulada</strong> y no podrás
                finalizarla.
              </DialogDescription>
            </DialogHeader>
            <DialogBody className="text-sm">
              <div className="rounded-md border bg-muted/30 px-3 py-2">
                <div className="font-medium">
                  {confirmAnular.fields.Articulo_DR || '—'}
                </div>
                <div className="text-xs text-muted-foreground">
                  {confirmAnular.cantidadEsperada} un. esperado
                </div>
              </div>
            </DialogBody>
            <DialogFooter>
              <Button
                variant="ghost"
                onClick={() => setConfirmAnular(null)}
                disabled={anularMutation.isPending}
              >
                Cancelar
              </Button>
              <Button
                variant="destructive"
                onClick={() => {
                  const target = confirmAnular;
                  setConfirmAnular(null);
                  anularMutation.mutate(target.id);
                }}
                disabled={anularMutation.isPending}
                className="gap-2"
              >
                Anular y cerrar recepción
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </Dialog>
  );
}

function HeaderSummary({ detalles }: { detalles: DetalleRow[] }) {
  const esperado = detalles.reduce((s, d) => s + d.cantidadEsperada, 0);
  const recibido = detalles.reduce((s, d) => s + d.cantidadRecibida, 0);
  return (
    <div className="flex items-center gap-3 text-xs">
      <div className="text-right">
        <div className="text-[10px] uppercase tracking-wide text-muted-foreground">
          Esperado
        </div>
        <div className="text-base font-semibold tabular-nums">{esperado}</div>
      </div>
      <div className="h-8 w-px bg-border" />
      <div className="text-right">
        <div className="text-[10px] uppercase tracking-wide text-muted-foreground">
          Recibido
        </div>
        <div
          className={cn(
            'text-base font-semibold tabular-nums',
            recibido === 0 && 'text-muted-foreground',
            recibido > 0 && recibido < esperado && 'text-amber-700',
            recibido >= esperado && esperado > 0 && 'text-emerald-700',
          )}
        >
          {recibido}
        </div>
      </div>
    </div>
  );
}

function LineCard({
  detalle,
  edit,
  onEditChange,
  onAnular,
  readOnly,
  busy,
}: {
  detalle: DetalleRow;
  edit: { cantidadRecibida: string; nroSerie: string } | undefined;
  onEditChange: (next: { cantidadRecibida: string; nroSerie: string }) => void;
  onAnular: () => void;
  readOnly: boolean;
  busy: boolean;
}) {
  const f = detalle.fields;
  const editVal = edit ?? {
    cantidadRecibida: String(detalle.cantidadRecibida),
    nroSerie: (f.NroSerie_DR ?? '').replace(/^-$/, ''),
  };
  const recibidaNum = parseNumber(editVal.cantidadRecibida);
  const over = recibidaNum > detalle.cantidadEsperada + 0.0001;
  const under = recibidaNum < detalle.cantidadEsperada && recibidaNum > 0;
  const complete = recibidaNum >= detalle.cantidadEsperada && detalle.cantidadEsperada > 0;

  return (
    <div
      className={cn(
        'rounded-md border bg-card p-3 transition-colors',
        complete && 'border-emerald-300 bg-emerald-50/40',
        over && 'border-amber-300 bg-amber-50/40',
      )}
    >
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="font-medium text-foreground">
            {detalle.modelo || f.Articulo_DR || '—'}
          </div>
          {detalle.modelo &&
            f.Articulo_DR &&
            detalle.modelo !== f.Articulo_DR && (
              <div className="text-xs text-muted-foreground">{f.Articulo_DR}</div>
            )}
          <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
            <span>
              Esperado:{' '}
              <span className="font-semibold text-foreground">
                {detalle.cantidadEsperada}
              </span>
            </span>
            {/* "Serie obligatoria" badge removed — capturing Nº Serie
                per-línea has been deprecated. The existing column on
                `21.DetalleRecepcion` stays for legacy data, but the
                UI no longer prompts for it. */}
          </div>
        </div>
        {!readOnly && (
          <button
            type="button"
            onClick={onAnular}
            disabled={busy}
            aria-label="Anular línea"
            title="Anular línea"
            className="inline-flex h-7 w-7 items-center justify-center rounded-md border text-muted-foreground transition-colors hover:border-destructive/40 hover:bg-destructive/10 hover:text-destructive disabled:opacity-50"
          >
            <X className="h-3.5 w-3.5" aria-hidden />
          </button>
        )}
      </div>
      {/* Nº Serie input removed — deprecated workflow. We keep the
          internal state so the save mutation echoes the existing
          `NroSerie_DR` value back unchanged (legacy líneas with a
          serie aren't wiped); only the editor is gone. */}
      <div className="mt-3 space-y-1">
        <Label htmlFor={`${detalle.id}-qty`} className="text-xs">
          Cantidad recibida
        </Label>
        <Input
          id={`${detalle.id}-qty`}
          type="number"
          inputMode="decimal"
          min={0}
          step="0.01"
          value={editVal.cantidadRecibida}
          onChange={(e) =>
            onEditChange({ ...editVal, cantidadRecibida: e.target.value })
          }
          className={cn(
            'tabular-nums',
            over && 'border-amber-400 bg-amber-50',
            complete && 'border-emerald-400 bg-emerald-50',
          )}
          disabled={readOnly || busy}
        />
        {over && (
          <p className="text-[11px] text-amber-700">
            <PackageCheck className="mr-1 inline h-3 w-3" aria-hidden />
            Se recibió más de lo esperado ({detalle.cantidadEsperada}).
          </p>
        )}
        {under && (
          <p className="text-[11px] text-muted-foreground">Parcial — faltan unidades.</p>
        )}
      </div>
    </div>
  );
}

function StatusBadge({ status }: { status: string }) {
  const cls =
    status === 'Pendiente'
      ? 'bg-amber-100 text-amber-700'
      : status === 'Recibido'
        ? 'bg-emerald-100 text-emerald-700'
        : status === 'Anulado'
          ? 'bg-rose-100 text-rose-700'
          : 'bg-secondary text-foreground';
  return (
    <span
      className={cn(
        'inline-flex rounded-md px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide',
        cls,
      )}
    >
      {status || '—'}
    </span>
  );
}

function parseNumber(raw: string): number {
  const trimmed = raw.trim();
  if (!trimmed) return 0;
  const n = Number(trimmed.replace(',', '.'));
  return Number.isFinite(n) && n >= 0 ? n : 0;
}
